import axios, { AxiosError } from 'axios';
import { API_BASE_URL } from 'constants/index';
import React, { useEffect, useState } from 'react';

interface IResponseData {
  data: Array<{
    id: number;
    code: string;
    code3?: string;
    name: string;
    phone_code: string;
    zone_id?: number;
    image_url: any;
    active: boolean;
    deleted_at: any;
    created_at: string;
    updated_at: string;
  }>;
  message: string;
  status_code: number;
}

interface IUseFetchPhoneCountryCodeProps {
  pause?: boolean;
}

export interface IOptions {
  label: string;
  value: string;
}

const useFetchPhoneCountryCode = (props: IUseFetchPhoneCountryCodeProps) => {
  const { pause } = props;
  const [isFetching, setIsFetching] = useState(true);
  const [data, setData] = useState<IResponseData['data']>();
  const [response, setResponse] = useState<IResponseData>();
  const [options, setOptions] = useState<IOptions[]>([]);

  const constructOptions = (data: IResponseData['data']): IOptions[] => {
    let result = [] as IOptions[];
    const priorityCodes = ['62', '60', '65'];

    for (const item of data) {
      if (!priorityCodes.includes(item.phone_code)) {
        const value = `+${item.phone_code}`;

        result.push({
          label: value,
          value,
        });
      }
    }

    if (result.length) {
      const priorityList = priorityCodes.map((code) => {
        const value = `+${code}`;

        return {
          label: value,
          value,
        };
      });

      result = [...priorityList, ...result];
    }

    return result;
  };

  const fetch = async () => {
    setIsFetching(true);

    const result = await axios
      .get<IResponseData>(`${API_BASE_URL}/postalcode/countries`)
      .then((res) => {
        const responseData = res.data;
        setData(responseData.data);
        setResponse(responseData);

        if (responseData.status_code !== 200) {
          return {
            isSuccess: false,
            message: responseData.message,
            data: responseData,
          };
        }

        const constructedOptions = constructOptions(responseData.data);
        setOptions(constructedOptions);

        return {
          isSuccess: true,
          message: responseData.message,
          data: responseData.data,
        };
      })
      .catch((err: AxiosError) => {
        const responseError = err.response?.data;
        setData(undefined);
        setResponse(responseError);
        return {
          isSuccess: false,
          message: 'Failed to fetch country code',
          data: null,
        };
      });

    setIsFetching(false);
    return result;
  };

  useEffect(() => {
    if (pause) {
      return;
    }

    fetch();
  }, [pause]);

  return {
    isFetching,
    data,
    response,
    options,
    fetch,
  };
};

export default useFetchPhoneCountryCode;
